import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {catchError, map, retry} from 'rxjs/operators';
import {HttpErrorHandler} from '../../http.error.handler';
import {environment} from '../../../environments/environment';
import {JsonApiResponse} from '../../shared/models/JsonApiResponse';
import {
  DeviceListItemBasic,
  VehicleBasic,
  VehicleModel,
  VehicleModelWithActiveShift,
  VehicleStats,
  VehicleUniqueCheckRequest,
  VehicleUniqueCheckResponse
} from '../../shared/models/vehicle.model';
import {VehicleCategoryModel} from '../../shared/models/vehicle';
import {VehicleImport} from '../../shared/models/vehicle-import';
import {firstValueFrom, Observable} from 'rxjs';
import {Page} from '../../shared/models/Page';

@Injectable({
  providedIn: 'root'
})
export class VehiclesService {
  constructor(private readonly http: HttpClient) {
  }

  // below are vehicle category services

  getVehicleCategory(id: number) {
    return firstValueFrom(this.http.get<JsonApiResponse<VehicleCategoryModel>>(`${environment.services.service}v1/vehicle/category/${id}`)
      .pipe(
        catchError(HttpErrorHandler.handleError), // then handle the error
        map(response => response.data)
      ));
  }

  getVehicleCategories() {
    return firstValueFrom(this.http.get<JsonApiResponse<VehicleCategoryModel[]>>(`${environment.services.service}v1/vehicle/category/list`)
      .pipe(
        retry(3), // retry a failed request up to 3 times
        catchError(HttpErrorHandler.handleError), // then handle the error
        map(response => response.data)
      ));
  }

  deleteVehicleCategory(id: number) {
    return firstValueFrom(this.http.delete<JsonApiResponse<null>>(`${environment.services.service}v1/vehicle/category/${id}`)
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  updateVehicleCategory(id: number, vehicleCategoryUpdate: VehicleCategoryModel): Observable<VehicleCategoryModel> {
    return this.http.put<JsonApiResponse<VehicleCategoryModel>>(
      `${environment.services.service}v1/vehicle/category/${id}`,
      vehicleCategoryUpdate,
      )
      .pipe(
        catchError(HttpErrorHandler.handleError), // then handle the error
        map(response => response.data)
      );
  }

  createVehicleCategory(vehicleCategory: VehicleCategoryModel): Promise<VehicleCategoryModel> {
    return firstValueFrom(this.http.post<JsonApiResponse<VehicleCategoryModel>>(
      `${environment.services.service}v1/vehicle/category`,
      vehicleCategory,
      )
      .pipe(
        catchError(HttpErrorHandler.handleError), // then handle the error
        map(response => response.data)
      ));
  }

  // below are vehicle services

  getVehiclesWithActiveShift() {
    return this.http.get<JsonApiResponse<VehicleModelWithActiveShift[]>>(`${environment.services.service}v1/vehicle/list-extended`)
      .pipe(
        retry(3), // retry a failed request up to 3 times
        catchError(HttpErrorHandler.handleError) // then handle the error
      );
  }

  getVehicle(id: number) {
    return firstValueFrom(
      this.http.get<JsonApiResponse<VehicleModel>>(`${environment.services.service}v1/vehicle/${id}`)
        .pipe(
          retry(3), // retry a failed request up to 3 times
          catchError(HttpErrorHandler.handleError), // then handle the error
          map(response => response.data)
        )
    );
  }

  getVehicles(groupId?: number, page?: number) {
    const options = {
      params: {} as any
    };
    if (groupId) {
      options.params.groupId = groupId;
    }
    if (page) {
      options.params.page = page;
    }
    return firstValueFrom(
      this.http.get<JsonApiResponse<Page<VehicleModel>>>(`${environment.services.service}v1/vehicle/list`, options)
        .pipe(
          retry(3), // retry a failed request up to 3 times
          catchError(HttpErrorHandler.handleError), // then handle the error
          map(response => response.data)
        )
    );
  }

  getVehiclesBasic(from: Date = null, to: Date = null) {
    let params = new HttpParams();
    if (from) {
      params = params.set('shiftStartedFrom', from.toISOString());
    }
    if (to) {
      params = params.set('shiftEndedTo', to.toISOString());
    }
    return firstValueFrom(
      this.http
        .get<JsonApiResponse<VehicleBasic[]>>(`${environment.services.service}v1/vehicle/list-basic`, {params})
        .pipe(
          retry(3), // retry a failed request up to 3 times
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  checkVehicleDataUniqueness(vehicle: VehicleModel) {
    const request: VehicleUniqueCheckRequest = {
      vehicleId: vehicle.id,
      vehicleName: vehicle.name,
      vehicleLabel: vehicle.label,
      vehiclePublicAlias: vehicle.publicAlias,
      lmuId: vehicle.lmuId,
      licensePlate: vehicle.licensePlate,
    };
    return this.http.post<JsonApiResponse<VehicleUniqueCheckResponse>>(`${environment.services.service}v1/vehicle/check-unique`, request)
      .pipe(
        catchError(HttpErrorHandler.handleError), // then handle the error
        map(response => response.data)
      );
  }

  listAvailableImeis(vehicleId: number, page?: number, search?: string) {
    let params = new HttpParams();

    if (page) {
      params = params.set('page', page);
    }
    if (search) {
      params = params.set('search', search);
    }

    const request = {
      params
    };
    return this.http.get<JsonApiResponse<Page<DeviceListItemBasic>>>(
      `${environment.services.service}v1/vehicle/${vehicleId}/list-available-imeis`,
      request,
      )
      .pipe(
        catchError(HttpErrorHandler.handleError), // then handle the error
        map(response => response.data)
      );
  }

  deleteVehicle(id: number) {
    return firstValueFrom(this.http.delete<JsonApiResponse<null>>(`${environment.services.service}v1/vehicle/${id}`)
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  updateVehicle(id: number, vehicleToUpdate: VehicleModel): Observable<JsonApiResponse<VehicleModel>> {
    return this.http.put<JsonApiResponse<VehicleModel>>(`${environment.services.service}v1/vehicle/${id}`, vehicleToUpdate)
        .pipe(
          catchError(HttpErrorHandler.handleError) // then handle the error
        );
  }

  createVehicle(vehicle: VehicleModel): Promise<JsonApiResponse<VehicleModel>> {
    return firstValueFrom(this.http.post<JsonApiResponse<VehicleModel>>(`${environment.services.service}v1/vehicle`, vehicle)
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  getVehicleStats(id: number, shiftId: number = null, from: Date = null, to: Date = null): Promise<JsonApiResponse<VehicleStats>> {
    let params = new HttpParams();
    if (shiftId != null) {
      params = params.set('shiftId', String(shiftId));
    }
    if (from) {
      params = params.set('from', from.toISOString());
    }
    if (to) {
      params = params.set('to', to.toISOString());
    }
    return firstValueFrom(this.http.get<JsonApiResponse<VehicleStats>>(`${environment.services.service}v1/vehicle/${id}/stats`, {params})
      .pipe(
        retry(3), // retry a failed request up to 3 times
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  validateVehicleImport(formData: any): Promise<JsonApiResponse<VehicleImport[]>> {
    return firstValueFrom(this.http.post<JsonApiResponse<VehicleImport[]>>(
      `${environment.services.service}v1/vehicle/import/validate`,
      formData,
      )
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  importVehicles(dataValidated: any) {
    return firstValueFrom(this.http.post<JsonApiResponse<any>>(`${environment.services.service}v1/vehicle/import`, dataValidated)
      .pipe(
        catchError(HttpErrorHandler.handleError), // then handle the error
      )
    );
  }
}
